import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  Renderer2,
  OnDestroy,
} from '@angular/core';

@Directive({
  selector: '[appTooltip]',
})
export class TooltipDirective implements OnDestroy {
  @Input('appTooltip') tooltipContent: string = '';
  @Input() tooltipOffsetX: number = 0;
  @Input() tooltipOffsetY: number = 0;
  private tooltip: HTMLElement | null = null;
  private resizeListener: (() => void) | null = null;

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (!this.tooltip) {
      this.tooltip = this.renderer.createElement('div');
      this.renderer.setProperty(this.tooltip, 'innerHTML', this.tooltipContent);

      this.setStyle();
      this.renderer.appendChild(document.body, this.tooltip);

      requestAnimationFrame(() => {
        this.setPosition();
      });

      this.resizeListener = this.renderer.listen('window', 'resize', () => {
        this.setStyle();     // ✅ Reapply style on resize
        this.setPosition();
      });
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.tooltip) {
      this.renderer.removeChild(document.body, this.tooltip);
      this.tooltip = null;

      if (this.resizeListener) {
        this.resizeListener();
        this.resizeListener = null;
      }
    }
  }

  ngOnDestroy(): void {
    if (this.tooltip) {
      this.renderer.removeChild(document.body, this.tooltip);
    }
    if (this.resizeListener) {
      this.resizeListener();
    }
  }

  private setStyle(): void {
    if (!this.tooltip) return;

    this.renderer.setStyle(this.tooltip, 'position', 'absolute');
    this.renderer.setStyle(this.tooltip, 'background', '#eaf7fa');
    this.renderer.setStyle(this.tooltip, 'bottom', 'auto');
    this.renderer.setStyle(this.tooltip, 'color', '#1a1a1a');
    this.renderer.setStyle(this.tooltip, 'border', '1px solid #3eaace');
    this.renderer.setStyle(this.tooltip, 'padding', '10px');
    this.renderer.setStyle(this.tooltip, 'border-radius', '5px');
    this.renderer.setStyle(this.tooltip, 'z-index', '1000');
    this.renderer.setStyle(this.tooltip, 'white-space', 'normal'); // allow wrapping
    this.renderer.setStyle(this.tooltip, 'font-size', '14px');
    this.renderer.setStyle(this.tooltip, 'pointer-events', 'none');
    this.renderer.setStyle(this.tooltip, 'box-sizing', 'border-box');

    // ✅ Responsive max-width
    const maxWidth = window.innerWidth <= 768 ? '300px' : '500px';
    this.renderer.setStyle(this.tooltip, 'max-width', maxWidth);
  }

  private setPosition(): void {
    if (!this.tooltip) return;

    const targetRect = this.el.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltip.getBoundingClientRect();

    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;

    let top = targetRect.bottom + scrollY + 8; 
    let left = targetRect.left + scrollX + (targetRect.width - tooltipRect.width) / 2;

    if (top + tooltipRect.height > window.innerHeight + scrollY) {
      top = targetRect.top + scrollY - tooltipRect.height - 8;
    }
    if (left + tooltipRect.width > window.innerWidth + scrollX) {
      left = window.innerWidth + scrollX - tooltipRect.width - 8;
    }
    if (left < scrollX + 8) {
      left = scrollX + 8;
    }

    this.renderer.setStyle(this.tooltip, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltip, 'left', `${left}px`);
  }
}
